/**
 * Component for displaying a single rule with validation status
 */

import { RULE_TYPES, OPERATORS, LITERATURE_TYPES } from '../../utils/ruleValidation';

function RuleItem({ rule, validation, onDelete, isCombined, isSelected, onSelect, onSuggest }) {
    const getRuleLabel = () => {
        // For combined rules, return the combined label
        if (rule.type === RULE_TYPES.COMBINED) {
            return rule.combinedLabel;
        }

        const operatorLabel = rule.operator === OPERATORS.MAX ? 'Max' : 'Min';

        switch (rule.type) {
            case RULE_TYPES.BOOKS_PER_AUTHOR:
                return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knihy'} od autora`;

            case RULE_TYPES.BOOKS_BY_TYPE:
                const typeLabels = {
                    PROSE: 'prózy',
                    POETRY: 'poezie',
                    DRAMA: 'dramat'
                };
                const typeLabel = typeLabels[rule.bookType] || rule.bookType;
                return `${operatorLabel} ${rule.value} ${typeLabel}`;

            case RULE_TYPES.BOOKS_BY_COUNTRY:
                if (rule.literatureType === LITERATURE_TYPES.CZECH) {
                    return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} české literatury`;
                } else if (rule.literatureType === LITERATURE_TYPES.WORLD) {
                    return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} světové literatury`;
                } else {
                    // Fallback for old rules
                    return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} z ${rule.country}`;
                }

            case RULE_TYPES.BOOKS_BY_YEAR_RANGE:
                let rangeDesc;
                if (rule.yearFrom && rule.yearTo) {
                    rangeDesc = `z let ${rule.yearFrom}-${rule.yearTo}`;
                } else if (rule.yearFrom) {
                    rangeDesc = `od roku ${rule.yearFrom}`;
                } else if (rule.yearTo) {
                    rangeDesc = `do roku ${rule.yearTo}`;
                } else {
                    rangeDesc = `z jakéhokoliv roku`;
                }
                return `${operatorLabel} ${rule.value} ${rule.value === 1 ? 'kniha' : 'knih'} ${rangeDesc}`;

            case RULE_TYPES.TOTAL_COUNT:
                return `${operatorLabel} ${rule.value} knih celkem`;

            default:
                return 'Neznámé pravidlo';
        }
    };

    const getValidationClass = () => {
        if (!validation) {
            return '';
        }

        if (validation.isSatisfied) {
            return 'rule-satisfied';
        }

        if (validation.severity === 'error') {
            return 'rule-error';
        }

        return 'rule-warning';
    };

    const handleRuleClick = (e) => {
        // Don't trigger selection if clicking on action buttons
        if (e.target.closest('.rule-delete-btn') || e.target.closest('.rule-suggest-btn')) {
            return;
        }
        if (onSelect) {
            onSelect(rule.id);
        }
    };

    // Show suggest button only for MIN rules that are not satisfied
    // Exclude BOOKS_PER_AUTHOR as suggestions are not supported for this type
    // For COMBINED rules, check if any of the nested rules is BOOKS_PER_AUTHOR
    const showSuggestButton = () => {
        if (!validation || validation.isSatisfied || !onSuggest) {
            return false;
        }

        // For combined rules, check operator and nested rules
        if (rule.type === RULE_TYPES.COMBINED) {
            // Only show for MIN combined rules
            if (!rule.combinedRules || rule.combinedRules.length === 0) {
                return false;
            }

            // Check if all nested rules have MIN operator
            const allMin = rule.combinedRules.every(r => r.operator === OPERATORS.MIN);
            if (!allMin) {
                return false;
            }

            // Don't show if any nested rule is BOOKS_PER_AUTHOR
            const hasAuthorRule = rule.combinedRules.some(r => r.type === RULE_TYPES.BOOKS_PER_AUTHOR);
            if (hasAuthorRule) {
                return false;
            }

            return true;
        }

        // For regular rules
        return (
            rule.operator === OPERATORS.MIN &&
            rule.type !== RULE_TYPES.BOOKS_PER_AUTHOR
        );
    };

    return (
        <div
            className={`rule-item ${isSelected ? 'rule-selected' : ''} ${isCombined ? 'rule-combined' : ''} ${onSelect ? 'rule-selectable' : ''} ${getValidationClass()}`}
            onClick={handleRuleClick}
        >
            <div className="rule-content">
                {isCombined && <span className="combined-icon">🔗</span>}
                <span className="rule-label">{getRuleLabel()}</span>
            </div>
            <div className="rule-actions">
                {showSuggestButton() && (
                    <button
                        className="rule-suggest-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSuggest(rule);
                        }}
                        title="Navrhnout dílo"
                    >
                        💡 Navrhnout dílo
                    </button>
                )}
                <button
                    className="rule-delete-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(rule.id);
                    }}
                    title="Smazat pravidlo"
                >
                    ✕
                </button>
            </div>
        </div>
    );
}

export default RuleItem;
